import {useQuery} from '@tanstack/react-query';
import {FC, useCallback, useContext, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {Link} from 'react-router-dom';
import {get, find, sumBy} from 'lodash';
import {Grid, Message, Breadcrumb} from 'semantic-ui-react';
import {Checkbox, DataFilter, DataFilteringContainerWithRouter as DataFilteringContainer,
  DataFilteringLayout, DataTable, FetchDataError, Loader, PermissionChecker,
  hasBlueprintPermissions, interpolateRoute, notifier, request} from 'apstra-ui-common';

import MultiParagraphText from '../../components/MultiParagraphText';
import IBAContext from '../IBAContext';
import {PredefinedDashboardIcons} from './PredefinedDashboardIcons';
import DashboardPreview from './DashboardPreview';
import DashboardsDisplayMode, {DASHBOARDS_DISPLAY_MODE} from './DashboardsDisplayMode';
import {apiQueryOptions} from '../../apiQueryOptions';
import generateDashboardURI from '../generateDashboardURI';
import {PredefinedDashboard} from '../types';
import userStore from '../../userStore';

import './AutoEnabledDashboards.less';

const tableSchema = [
  {
    name: 'name',
    label: 'Name',
    value: ({item, name}) => get(item, ['schema', 'properties', 'label', 'default'], name),
    sortable: true,
    formatter: ({item, value}: {item: PredefinedDashboard, value: string}) => (
      <>
        <PredefinedDashboardIcons predefinedDashboard={item} />
        &nbsp;
        {value}
      </>
    ),
  },
  {
    name: 'description',
    label: 'Description',
    value: ['description'],
    formatter: ({value, item: predefinedDashboard}) => {
      const conditionDescription = get(predefinedDashboard, ['condition', 'description']);
      return (
        <small>
          <MultiParagraphText asMarkdown text={value} />
          {conditionDescription && <MultiParagraphText asMarkdown text={`Condition: ${conditionDescription}`} />}
        </small>
      );
    }
  },
  {
    name: 'widgets',
    label: 'Widgets',
    formatter: ({item: predefinedDashboard, value, params: {dashboardPreview}}:
      {item: PredefinedDashboard, value: any, params: any}) =>
        dashboardPreview ? <DashboardPreview dashboard={predefinedDashboard} /> : value,
    value: ({item}: {item: PredefinedDashboard}) => item.autoscaling || item.dynamic ? '' : sumBy(item.grid, 'length'),
    sortable: true,
  },
  {
    name: 'enabled',
    label: 'Auto-Enable',
    value: ({item}) => !get(item, ['disabled']),
    formatter: ({item, value, params: {
      blueprintPermissions, dashboardNameToggleInProgress, toggleDashboard, predefinedDashboards, refetchData
    }}) => (
      <PermissionChecker
        hasPermissions={hasBlueprintPermissions({blueprintPermissions, action: 'edit'})}
        popupProps={{}}
      >
        <Checkbox
          toggle
          checked={value}
          aria-label='Auto-Enable'
          disabled={
            dashboardNameToggleInProgress === item.name ||
            item.condition.type === 'never'
          }
          onChange={() => toggleDashboard({
            dashboardName: item.name,
            disabled: value,
            predefinedDashboards,
            refetchData,
          })}
        />
      </PermissionChecker>
    ),
  },
  {
    name: 'removable',
    label: 'Auto-Removable',
    value: ({item}: {item: PredefinedDashboard}) => item.condition.properties?.remove ?? false,
    formatter: ({value}) => (
      <Checkbox
        readOnly
        disabled
        toggle
        checked={value}
        aria-label='Auto-Removable'
      />
    ),
  }
];

const userStoreKey = 'autoEnabledDashboards';

const AutoEnabledDashboards: FC = observer(() => {
  const {blueprintId, blueprintPermissions, routes} = useContext(IBAContext);

  const {data, isLoading, error, refetch} = useQuery({
    ...apiQueryOptions.predefinedDashboardList(blueprintId),
    queryFn: ({signal}) => request(interpolateRoute(routes.predefinedDashboardList, {blueprintId}), {signal}),
  });

  const predefinedDashboards = data?.items ?? [];

  const [dashboardNameToggleInProgress, setDashboardNameToggleInProgress] = useState(null);

  const breadcrumbSections = useMemo(() => [
    {key: 'home', content: 'Dashboards', as: Link, to: generateDashboardURI({blueprintId})},
    {key: 'label', content: 'Auto-Enabled Dashboards', active: true},
  ], [blueprintId]);

  const toggleDashboard = useCallback(async ({dashboardName, disabled, predefinedDashboards, refetchData}) => {
    setDashboardNameToggleInProgress(dashboardName);
    let updateError: any = null;
    try {
      await request(
        interpolateRoute(routes.predefinedDashboard, {blueprintId, predefinedDashboard: dashboardName}),
        {
          method: 'PATCH',
          body: JSON.stringify({disabled})
        }
      );
    } catch (error) {
      updateError = error;
    }
    if (updateError) {
      notifier.showError(updateError);
    } else {
      notifier.notify({
        resourceLabel: get(
          find(predefinedDashboards, {name: dashboardName}),
          ['schema', 'properties', 'label', 'default'],
          dashboardName
        ),
        message: `${disabled ? 'Disabled' : 'Enabled'} successfully`
      });
    }
    try {
      await refetchData();
    } finally {
      setDashboardNameToggleInProgress(null);
    }
  }, [blueprintId, routes.predefinedDashboard]);

  const defaultPageSize = userStore.getStoreValue([userStoreKey, 'pageSize']);

  return (
    <DataFilteringContainer
      stateQueryParam='auto-enabled-dashboards-filter'
      defaultPageSize={defaultPageSize}
      defaultFilters={{displayMode: DASHBOARDS_DISPLAY_MODE.preview}}
      defaultSorting={{name: 'asc'}}
      setUserStoreProps={userStore.setStoreValueFn(userStoreKey)}
    >
      {({activePage, pageSize, updatePagination, sorting, updateSorting, filters, updateFilters}) =>
        isLoading ? <Loader />
          : error ? <FetchDataError error={error} />
          : (
            <Grid>
              <Grid.Column width={16}>
                <Breadcrumb
                  size='big'
                  icon='right caret'
                  sections={breadcrumbSections}
                />
              </Grid.Column>
              <Grid.Column width={16}>
                <DataFilter
                  items={predefinedDashboards}
                  schema={tableSchema}
                  activePage={activePage}
                  pageSize={pageSize}
                  sorting={sorting}
                >
                  {({items: filteredPredefinedDashboards, totalCount}) =>
                    <DataFilteringLayout
                      paginationProps={{
                        totalCount,
                        activePage,
                        pageSize,
                        onChange: updatePagination
                      }}
                      extraContent={{
                        buttonsPanel: (
                          <DashboardsDisplayMode
                            value={filters.displayMode}
                            onChange={(displayMode) => updateFilters({...filters, displayMode})}
                            modes={[
                              DASHBOARDS_DISPLAY_MODE.summary,
                              DASHBOARDS_DISPLAY_MODE.preview,
                            ]}
                          />
                        )
                      }}
                    >
                      {predefinedDashboards.length ?
                        <div className='auto-enabled-dashboards-table-container'>
                          <DataTable
                            items={filteredPredefinedDashboards}
                            schema={tableSchema}
                            params={{
                              routes, blueprintPermissions, dashboardNameToggleInProgress,
                              dashboardPreview: filters.displayMode === DASHBOARDS_DISPLAY_MODE.preview,
                              toggleDashboard, predefinedDashboards, refetchData: refetch,
                            }}
                            sorting={sorting}
                            updateSorting={updateSorting}
                          />
                        </div>
                      :
                        <Message
                          info
                          icon='info circle'
                          content='There are currently no dashboards defined.'
                        />
                      }
                    </DataFilteringLayout>
                  }
                </DataFilter>
              </Grid.Column>
            </Grid>
          )
      }
    </DataFilteringContainer>
  );
});

export default AutoEnabledDashboards;
